import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  input,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';

export interface TableColumn<T> {
  key: keyof T | string;
  label: string;
  sortable?: boolean;
  width?: string;
}

export interface SortEvent {
  column: string;
  direction: 'asc' | 'desc';
}

@Component({
  selector: 'app-data-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, LoadingSpinnerComponent, EmptyStateComponent],
  template: `
    <div class="table-container">
      @if (loading()) {
        <div class="loading-overlay">
          <app-loading-spinner size="lg" />
        </div>
      }

      <table class="data-table" [attr.aria-busy]="loading()">
        <thead>
          <tr>
            @for (col of columns(); track col.key) {
              <th
                [style.width]="col.width"
                [class.sortable]="col.sortable"
                [attr.aria-sort]="getSortDirection(col.key.toString())"
                (click)="col.sortable && onSort(col.key.toString())"
                (keydown.enter)="col.sortable && onSort(col.key.toString())"
                [tabindex]="col.sortable ? 0 : -1">
                {{ col.label }}
                @if (col.sortable) {
                  <span class="sort-icon" aria-hidden="true">
                    @if (sortColumn() === col.key) {
                      {{ sortDirection() === 'asc' ? '↑' : '↓' }}
                    }
                  </span>
                }
              </th>
            }
          </tr>
        </thead>
        <tbody>
          @if (data().length === 0 && !loading()) {
            <tr>
              <td [attr.colspan]="columns().length">
                <app-empty-state
                  [icon]="emptyIcon()"
                  [title]="emptyTitle()"
                  [message]="emptyMessage()" />
              </td>
            </tr>
          } @else {
            @for (row of data(); track trackByFn()(row); let idx = $index) {
              <tr
                [class.clickable]="rowClickable()"
                (click)="rowClickable() && onRowClick(row)"
                (keydown.enter)="rowClickable() && onRowClick(row)"
                [tabindex]="rowClickable() ? 0 : -1">
                @for (col of columns(); track col.key) {
                  <td>
                    @if (cellTemplate(); as template) {
                      <ng-container
                        *ngTemplateOutlet="template; context: { $implicit: row, column: col.key, index: idx }" />
                    } @else {
                      {{ getCellValue(row, col.key.toString()) }}
                    }
                  </td>
                }
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
  `,
  styles: `
    .table-container {
      position: relative;
      overflow-x: auto;
    }

    .loading-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgb(255 255 255 / 0.8);
      z-index: 10;
    }

    .data-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.875rem;
    }

    th {
      text-align: left;
      padding: 0.75rem 1rem;
      font-weight: 600;
      color: oklch(0.4 0 0);
      background: oklch(0.97 0 0);
      border-bottom: 1px solid oklch(0.9 0 0);
      white-space: nowrap;
    }

    th.sortable {
      cursor: pointer;
      user-select: none;
    }

    th.sortable:hover {
      background: oklch(0.95 0 0);
    }

    th.sortable:focus-visible {
      outline: 2px solid oklch(0.5 0.2 250);
      outline-offset: -2px;
    }

    .sort-icon {
      display: inline-block;
      width: 1rem;
      margin-left: 0.25rem;
      color: oklch(0.5 0.15 250);
    }

    td {
      padding: 0.75rem 1rem;
      color: oklch(0.3 0 0);
      border-bottom: 1px solid oklch(0.93 0 0);
    }

    tbody tr.clickable {
      cursor: pointer;
    }

    tbody tr.clickable:hover {
      background: oklch(0.98 0.01 250);
    }

    tbody tr.clickable:focus-visible {
      outline: 2px solid oklch(0.5 0.2 250);
      outline-offset: -2px;
    }
  `,
})
export class DataTableComponent<T extends Record<string, unknown>> {
  readonly columns = input.required<TableColumn<T>[]>();
  readonly data = input.required<T[]>();
  readonly loading = input(false);
  readonly rowClickable = input(false);
  readonly trackByFn = input<(item: T) => unknown>(() => (item: T) => item);

  readonly emptyIcon = input('inbox');
  readonly emptyTitle = input('No data');
  readonly emptyMessage = input<string>();

  readonly sortChange = output<SortEvent>();
  readonly rowClick = output<T>();

  readonly cellTemplate = contentChild<TemplateRef<{ $implicit: T; column: string; index: number }>>(
    'cell'
  );

  protected readonly sortColumn = signal<string | null>(null);
  protected readonly sortDirection = signal<'asc' | 'desc'>('asc');

  protected getSortDirection(column: string): 'ascending' | 'descending' | 'none' {
    if (this.sortColumn() !== column) return 'none';
    return this.sortDirection() === 'asc' ? 'ascending' : 'descending';
  }

  protected onSort(column: string): void {
    if (this.sortColumn() === column) {
      this.sortDirection.update((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }

    this.sortChange.emit({
      column,
      direction: this.sortDirection(),
    });
  }

  protected onRowClick(row: T): void {
    this.rowClick.emit(row);
  }

  protected getCellValue(row: T, key: string): unknown {
    const keys = key.split('.');
    let value: unknown = row;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        return '';
      }
    }

    return value;
  }
}
